import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

// We use JWT Bearer tokens (stored in localStorage, sent in Authorization header),
// not cookies. So we leave withCredentials unset → CORS is simpler and works with
// any Emergent ingress config.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      const { data } = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(data);
    } catch {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    localStorage.setItem('token', data.token);
    // Re-fetch /api/auth/me so the user object is enriched with the same
    // fields as on a refresh (shop_type, tier, company_name). Without this
    // step shop-specific nav items (e.g. Garnbutikk) wouldn't show until
    // the user reloads the page.
    try {
      const me = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${data.token}` },
      });
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  const register = async (email, password, name, invite_code) => {
    const { data } = await axios.post(
      `${API}/api/auth/register`,
      { email, password, name, invite_code },
    );
    localStorage.setItem('token', data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API}/api/auth/logout`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
    } catch {}
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
